/**
 * The message log (owner and managers). Every message to or from the team and the
 * doctors, on every channel, with what happened to it. Built for "we never got it":
 * pick the person, see everything they were sent and whether it arrived.
 */
import { useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { AlertTriangle, ArrowDownLeft, Bell, Check, CheckCheck, ChevronDown, Clock, Mail, MessageCircle, Search, Send, X } from 'lucide-react';
import { PageHeader, Card } from '@/components/ui/Card';
import Button from '@/components/ui/Button';
import Select from '@/components/ui/Select';
import { cn } from '@/lib/cn';
import { fetchLogPeople, fetchMessageLog, type LogFilters, type LogRow } from '@/lib/connections';
import Lines from '@/components/connect/Lines';

const CHANNELS = [
  { v: '', label: 'All channels', icon: null },
  { v: 'whatsapp', label: 'WhatsApp', icon: MessageCircle },
  { v: 'telegram', label: 'Telegram', icon: Send },
  { v: 'email', label: 'Email', icon: Mail },
  { v: 'in_app', label: 'In the app', icon: Bell },
] as const;

const STATUS: Record<string, { label: string; tone: string; icon: typeof Check }> = {
  read: { label: 'Read', tone: 'text-sky-300 border-sky-500/40 bg-sky-500/10', icon: CheckCheck },
  delivered: { label: 'Delivered', tone: 'text-emerald-300 border-emerald-500/40 bg-emerald-500/10', icon: CheckCheck },
  sent: { label: 'Sent', tone: 'text-slate-200 border-slate-600 bg-slate-700/30', icon: Check },
  received: { label: 'Reply', tone: 'text-violet-300 border-violet-500/40 bg-violet-500/10', icon: ArrowDownLeft },
  failed: { label: 'Failed', tone: 'text-red-300 border-red-500/40 bg-red-500/10', icon: AlertTriangle },
  skipped: { label: 'Not sent', tone: 'text-amber-300 border-amber-500/40 bg-amber-500/10', icon: AlertTriangle },
  pending: { label: 'Waiting', tone: 'text-amber-300 border-amber-500/40 bg-amber-500/10', icon: Clock },
};
const STATUS_ORDER = ['failed', 'skipped', 'sent', 'delivered', 'read', 'received'];

const PAGE = 60;

function when(iso: string) {
  const d = new Date(iso);
  const mins = Math.round((Date.now() - d.getTime()) / 60000);
  const rel = mins < 1 ? 'just now' : mins < 60 ? `${mins} min ago` : mins < 1440 ? `${Math.round(mins / 60)} h ago` : `${Math.round(mins / 1440)} d ago`;
  return { rel, exact: d.toLocaleString('en-AE', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) };
}

function ChannelIcon({ c }: { c: LogRow['channel'] }) {
  const Icon = c === 'whatsapp' ? MessageCircle : c === 'telegram' ? Send : c === 'email' ? Mail : Bell;
  const tone = c === 'whatsapp' ? 'text-emerald-300 bg-emerald-500/10' : c === 'telegram' ? 'text-sky-300 bg-sky-500/10' : c === 'email' ? 'text-amber-200 bg-amber-500/10' : 'text-slate-300 bg-slate-700/40';
  return <span className={cn('w-8 h-8 rounded-lg flex items-center justify-center shrink-0', tone)} title={c.replace('_', '-')}><Icon size={15} /></span>;
}

function Row({ r }: { r: LogRow }) {
  const [open, setOpen] = useState(false);
  const s = STATUS[r.status] ?? STATUS.sent;
  const t = when(r.at);
  return (
    <div className={cn('py-3 flex gap-3', r.direction === 'in' && 'pl-4 border-l-2 border-violet-500/40')} data-testid="log-row">
      <ChannelIcon c={r.channel} />
      <div className="flex-1 min-w-0">
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
          <span className="text-sm text-muted-strong">{r.direction === 'in' ? `${r.person ?? r.address} replied` : (r.person ?? r.address ?? 'Unknown')}</span>
          {r.clinic && <span className="text-[11px] text-muted">{r.clinic}</span>}
          <span className={cn('inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px]', s.tone)}><s.icon size={11} />{s.label}</span>
          {r.creative_code && <Link to={`/creatives/${r.creative_id}`} className="text-[11px] font-mono text-accent-400 hover:underline">{r.creative_code}</Link>}
          <span className="ml-auto text-[11px] text-muted tabular-nums" title={t.exact}>{t.rel}</span>
        </div>
        <button className="text-left w-full mt-0.5" onClick={() => setOpen(!open)} aria-expanded={open}>
          <div className="text-sm text-muted-strong/90 truncate" dir="auto">{(r.direction === 'in' ? r.body : r.title) || r.body.split('\n')[0] || '(no text)'}</div>
          {!open && r.body && r.direction === 'out' && <div className="text-xs text-muted truncate" dir="auto">{r.body.replace(/\s+/g, ' ')}</div>}
        </button>
        {r.error && (r.error.startsWith('dry-run')
          ? <div className="mt-1 text-[11px] text-muted">Test mode: recorded, not actually sent.</div>
          : <div className="mt-1 text-xs text-red-300 flex items-start gap-1"><AlertTriangle size={12} className="mt-0.5 shrink-0" />{r.error}</div>)}
        {open && (
          <div className="mt-2 rounded-lg bg-bg border border-slate-800 p-3 text-xs space-y-2">
            {r.body && <Lines text={r.body} className="text-muted-strong" />}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-muted">
              <div><div className="text-[10px] uppercase tracking-wide">Channel</div>{CHANNELS.find((c) => c.v === r.channel)?.label}</div>
              <div><div className="text-[10px] uppercase tracking-wide">{r.direction === 'in' ? 'From' : 'To'}</div>{r.address ?? 'their inbox in the app'}</div>
              <div><div className="text-[10px] uppercase tracking-wide">Sent</div>{t.exact}</div>
              <div><div className="text-[10px] uppercase tracking-wide">{r.read_at ? 'Read' : 'Delivered'}</div>{r.read_at ? when(r.read_at).exact : r.delivered_at && r.channel === 'whatsapp' ? when(r.delivered_at).exact : r.channel === 'whatsapp' ? 'not yet' : 'not reported by this channel'}</div>
            </div>
            {r.kind && <div className="text-[11px] text-muted">Type: {r.kind.replace(/_/g, ' ')}</div>}
          </div>
        )}
      </div>
    </div>
  );
}

export default function MessagesPage() {
  const [params, setParams] = useSearchParams();
  const f: LogFilters = {
    person: params.get('person') || undefined, channel: params.get('channel') || undefined,
    status: params.get('status') || undefined, direction: params.get('direction') || undefined,
    creative: params.get('creative') || undefined, q: params.get('q') || undefined,
    since: params.get('since') || undefined, until: params.get('until') || undefined,
  };
  const set = (k: string, v?: string) => {
    const n = new URLSearchParams(params);
    if (v) n.set(k, v); else n.delete(k);
    setParams(n, { replace: true });
  };
  const [text, setText] = useState(f.q ?? '');
  const [code, setCode] = useState(f.creative ?? '');
  const people = useQuery({ queryKey: ['log-people'], queryFn: fetchLogPeople });
  const q = useInfiniteQuery({
    queryKey: ['message-log', f],
    queryFn: ({ pageParam }) => fetchMessageLog({ ...f, since: f.since ? `${f.since}T00:00:00+04:00` : undefined,
      until: f.until ? `${f.until}T23:59:59+04:00` : undefined, limit: PAGE, offset: pageParam }),
    initialPageParam: 0,
    getNextPageParam: (last, all) => (all.length * PAGE < last.total ? all.length * PAGE : undefined),
    refetchInterval: 30_000,
  });
  const first = q.data?.pages[0];
  const rows = useMemo(() => q.data?.pages.flatMap((p) => p.rows) ?? [], [q.data]);
  const person = (people.data ?? []).find((p) => p.key === f.person);
  const anyFilter = Object.values(f).some(Boolean);

  return (
    <div className="max-w-5xl">
      <PageHeader title="Message log" subtitle={person ? `Everything sent to ${person.name}${person.clinic ? `, ${person.clinic}` : ''}, and their replies.` : 'Every message to the team and the doctors, on every channel, and what happened to it.'} />

      <div className="flex flex-wrap gap-2 mb-4" data-testid="log-counts">
        {STATUS_ORDER.map((k) => {
          const n = first?.counts?.[k] ?? 0;
          const s = STATUS[k];
          const active = f.status === k;
          return (
            <button key={k} onClick={() => set('status', active ? undefined : k)}
              className={cn('rounded-xl border px-3 py-2 text-left min-w-[92px] transition', active ? s.tone : 'border-slate-800 bg-bg-card hover:border-slate-600')}>
              <div className="text-lg tabular-nums text-muted-strong">{n}</div>
              <div className="text-[11px] text-muted flex items-center gap-1"><s.icon size={11} />{s.label}</div>
            </button>
          );
        })}
      </div>

      <Card className="mb-4">
        <div className="grid gap-3 md:grid-cols-[minmax(0,1.3fr)_minmax(0,1fr)_minmax(0,1fr)]">
          <Select label="Person" value={f.person ?? ''} onChange={(v) => set('person', v || undefined)} searchable placeholder="Everyone"
            options={[{ value: '', label: 'Everyone' }, ...(people.data ?? []).map((p) => ({ value: p.key, label: p.name,
              hint: p.kind === 'staff' ? 'Team' : p.kind === 'doctor' ? `Doctor · ${p.clinic}` : `Clinic · ${p.clinic}` }))]} />
          <div className="space-y-1.5">
            <div className="text-xs text-muted">From</div>
            <input type="date" value={f.since ?? ''} onChange={(e) => set('since', e.target.value || undefined)}
              className="w-full bg-bg border border-slate-800 rounded-lg px-3 py-2 text-sm text-muted-strong" data-testid="log-since" />
          </div>
          <div className="space-y-1.5">
            <div className="text-xs text-muted">To</div>
            <input type="date" value={f.until ?? ''} onChange={(e) => set('until', e.target.value || undefined)}
              className="w-full bg-bg border border-slate-800 rounded-lg px-3 py-2 text-sm text-muted-strong" />
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2 mt-3">
          {CHANNELS.map((c) => (
            <button key={c.v} onClick={() => set('channel', c.v || undefined)} data-testid={`log-channel-${c.v || 'all'}`}
              className={cn('text-xs px-2.5 py-1 rounded-full border', (f.channel ?? '') === c.v ? 'border-accent-400 text-accent-300 bg-accent-600/15' : 'border-slate-700 text-muted')}>
              {c.label}
            </button>
          ))}
          <span className="w-px h-5 bg-slate-800 mx-1" />
          {[['', 'Both ways'], ['out', 'Sent'], ['in', 'Replies']].map(([v, label]) => (
            <button key={v} onClick={() => set('direction', v || undefined)}
              className={cn('text-xs px-2.5 py-1 rounded-full border', (f.direction ?? '') === v ? 'border-accent-400 text-accent-300 bg-accent-600/15' : 'border-slate-700 text-muted')}>
              {label}
            </button>
          ))}
        </div>
        <div className="flex flex-wrap items-center gap-2 mt-3">
          <form className="relative flex-1 min-w-[200px]" onSubmit={(e) => { e.preventDefault(); set('q', text.trim() || undefined); }}>
            <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted" />
            <input value={text} onChange={(e) => setText(e.target.value)} placeholder="Search the text or the failure reason, then Enter" dir="auto"
              className="w-full bg-bg border border-slate-800 rounded-lg pl-8 pr-2 py-2 text-sm text-muted-strong" data-testid="log-search" />
          </form>
          <form onSubmit={(e) => { e.preventDefault(); set('creative', code.trim() || undefined); }}>
            <input value={code} onChange={(e) => setCode(e.target.value)} placeholder="Piece code, e.g. MS-6-2026-004"
              className="w-56 bg-bg border border-slate-800 rounded-lg px-3 py-2 text-sm text-muted-strong font-mono" />
          </form>
          {anyFilter && <Button size="sm" variant="ghost" icon={<X size={12} />} onClick={() => { setText(''); setCode(''); setParams(new URLSearchParams(), { replace: true }); }}>Clear filters</Button>}
        </div>
      </Card>

      <Card>
        <div className="flex items-center justify-between mb-1">
          <div className="text-xs text-muted" data-testid="log-total">{first ? `${first.total} message${first.total === 1 ? '' : 's'}` : ' '}</div>
          <div className="text-[11px] text-muted">Updates every 30 seconds</div>
        </div>
        {q.isLoading && <div className="text-sm text-muted py-6">Loading...</div>}
        {!q.isLoading && rows.length === 0 && <div className="text-sm text-muted py-10 text-center">Nothing matches. {anyFilter ? 'Try clearing a filter.' : ''}</div>}
        <div className="divide-y divide-slate-800/60">
          {rows.map((r) => <Row key={`${r.channel}:${r.id}`} r={r} />)}
        </div>
        {q.hasNextPage && (
          <div className="pt-3 flex justify-center">
            <Button variant="secondary" size="sm" icon={<ChevronDown size={12} />} onClick={() => q.fetchNextPage()} loading={q.isFetchingNextPage}>Older messages</Button>
          </div>
        )}
      </Card>
    </div>
  );
}
